import { DateTime } from "luxon";
import { EFrequencyType, EIntervalType, EMode, ESchedulerMode, EWeekdays } from "../../../enums/event.enums";
import { buildEventPayload } from "../../../helpers/eventPayloadBuilder";
import { IEvent, IExclusionRule } from "../../../interfaces/event.interfaces";

export type RepetitiveExecutionFormState = {
  frequencyType: EFrequencyType | "hourly" | "eachNDays" | "eachNMinutes" | "eachNHours";
  intervalValue: string;
  each: number;
  at: string; // "HH:mm"
  weekdays: EWeekdays[];
  monthDays: string[]; // ["01", "17", ...]
  yearDates: string[]; // ["12-25", ...]
  endDate: string; // ISO with offset
  exclusions: IExclusionRule[];
};

export type RecurrencyPreset = {
  id:
    | "monthly-today"
    | "monthly-1st"
    | "monthly-15th"
    | "weekly-today"
    | "weekly-monday"
    | "weekly-friday"
    | "weekly-saturday"
    | "daily"
    | "weekdays"
    | "first-friday"
    | "last-friday"
    | "first-monday"
    | "last-monday"
    | "weekly-on"
    | "custom";
  label: string;
  payload?: IEvent;
  apply?: (current: RepetitiveExecutionFormState) => RepetitiveExecutionFormState;
  parentId?: "weekly-on";
};

const ZONE = "UTC-6";

function ordinal(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n}st`;
  if (mod10 === 2 && mod100 !== 12) return `${n}nd`;
  if (mod10 === 3 && mod100 !== 13) return `${n}rd`;
  return `${n}th`;
}

function toWeekdayEnum(now: DateTime): EWeekdays {
  // Luxon: Monday=1 ... Sunday=7
  switch (now.weekday) {
    case 1:
      return EWeekdays.MONDAY;
    case 2:
      return EWeekdays.TUESDAY;
    case 3:
      return EWeekdays.WEDNESDAY;
    case 4:
      return EWeekdays.THURSDAY;
    case 5:
      return EWeekdays.FRIDAY;
    case 6:
      return EWeekdays.SATURDAY;
    case 7:
    default:
      return EWeekdays.SUNDAY;
  }
}

function roundUpToNext30Min(now: DateTime): string {
  const base = now.set({ second: 0, millisecond: 0 });
  const nextMinute = Math.ceil(base.minute / 30) * 30;
  const rounded = nextMinute === 60 ? base.plus({ hours: 1 }).set({ minute: 0 }) : base.set({ minute: nextMinute });
  return rounded.toFormat("HH:mm");
}

function rangeExclusions(startDay: number, endDay: number): IExclusionRule[] {
  // Exclude every day-of-month outside [startDay, endDay].
  // This enables patterns like "first Friday of every month":
  // - schedule weekly on Friday
  // - exclude 08..31 so only dates 01..07 remain.
  const rules: IExclusionRule[] = [];
  for (let d = 1; d < startDay; d += 1) rules.push({ type: EIntervalType.DATE_DAY, value: d.toString().padStart(2, "0") });
  for (let d = endDay + 1; d <= 31; d += 1) rules.push({ type: EIntervalType.DATE_DAY, value: d.toString().padStart(2, "0") });
  return rules;
}

function buildPresetPayload(state: RepetitiveExecutionFormState): IEvent {
  // NOTE: We include a minimal FIXED schedule time to keep the payload immediately usable.
  return buildEventPayload({
    mode: EMode.REPETITIVE,
    schedulerMode: ESchedulerMode.FIXED,
    fixedTimes: [state.at],
    duration: 90,
    endDate: state.endDate,
    frequencyType: state.frequencyType as EFrequencyType,
    at: state.at,
    weekdays: state.weekdays,
    monthDays: state.monthDays,
    yearDates: state.yearDates,
    exclusions: state.exclusions,
    each: state.each,
  });
}

export function getRecurrencyPresets(nowInput: DateTime = DateTime.now().setZone(ZONE)): RecurrencyPreset[] {
  const now = nowInput.setZone(ZONE);
  const at = roundUpToNext30Min(now);

  const dayOfMonth = now.day;
  const dayStr = dayOfMonth.toString().padStart(2, "0");
  const weekdayName = now.toFormat("cccc");
  const weekdayEnum = toWeekdayEnum(now);

  const makeNext = (patch: Partial<RepetitiveExecutionFormState>) => {
    return (current: RepetitiveExecutionFormState): RepetitiveExecutionFormState => {
      return {
        ...current,
        intervalValue: current.intervalValue ?? "01",
        each: current.each ?? 1,
        exclusions: current.exclusions ?? [],
        weekdays: [],
        monthDays: [],
        yearDates: [],
        ...patch,
      };
    };
  };

  const monthlyTodayApply = makeNext({
    frequencyType: EFrequencyType.MONTHLY,
    monthDays: [dayStr],
    each: 1,
    at,
  });

  const monthly1stApply = makeNext({
    frequencyType: EFrequencyType.MONTHLY,
    monthDays: ["01"],
    each: 1,
    at,
  });

  const monthly15thApply = makeNext({
    frequencyType: EFrequencyType.MONTHLY,
    monthDays: ["15"],
    each: 1,
    at,
  });

  const weeklyTodayApply = makeNext({
    frequencyType: EFrequencyType.WEEKLY,
    weekdays: [weekdayEnum],
    each: 1,
    at,
  });

  const weeklyMondayApply = makeNext({
    frequencyType: EFrequencyType.WEEKLY,
    weekdays: [EWeekdays.MONDAY],
    each: 1,
    at,
  });

  const weeklyFridayApply = makeNext({
    frequencyType: EFrequencyType.WEEKLY,
    weekdays: [EWeekdays.FRIDAY],
    each: 1,
    at,
  });

  const weeklySaturdayApply = makeNext({
    frequencyType: EFrequencyType.WEEKLY,
    weekdays: [EWeekdays.SATURDAY],
    each: 1,
    at,
  });

  const dailyApply = makeNext({
    frequencyType: EFrequencyType.DAILY,
    at,
  });

  const weekdaysApply = makeNext({
    frequencyType: EFrequencyType.WEEKLY,
    weekdays: [EWeekdays.MONDAY, EWeekdays.TUESDAY, EWeekdays.WEDNESDAY, EWeekdays.THURSDAY, EWeekdays.FRIDAY],
    each: 1,
    at,
  });

  const weekendsApply = makeNext({
    frequencyType: EFrequencyType.WEEKLY,
    weekdays: [EWeekdays.SATURDAY, EWeekdays.SUNDAY],
    each: 1,
    at,
  });

  const firstFridayApply = makeNext({
    frequencyType: EFrequencyType.WEEKLY,
    weekdays: [EWeekdays.FRIDAY],
    each: 1,
    at,
    exclusions: rangeExclusions(1, 7),
  });

  const lastFridayApply = makeNext({
    frequencyType: EFrequencyType.WEEKLY,
    weekdays: [EWeekdays.FRIDAY],
    each: 1,
    at,
    exclusions: rangeExclusions(25, 31),
  });

  const firstMondayApply = makeNext({
    frequencyType: EFrequencyType.WEEKLY,
    weekdays: [EWeekdays.MONDAY],
    each: 1,
    at,
    exclusions: rangeExclusions(1, 7),
  });

  const lastMondayApply = makeNext({
    frequencyType: EFrequencyType.WEEKLY,
    weekdays: [EWeekdays.MONDAY],
    each: 1,
    at,
    exclusions: rangeExclusions(25, 31),
  });

  const baseEndDate = DateTime.now().plus({ months: 2 }).setZone(ZONE).toISO({ includeOffset: true }) as string;

  const materialize = (apply: (current: RepetitiveExecutionFormState) => RepetitiveExecutionFormState) => {
    const next = apply({
      frequencyType: EFrequencyType.WEEKLY,
      intervalValue: "01",
      each: 1,
      at,
      weekdays: [],
      monthDays: [],
      yearDates: [],
      endDate: baseEndDate,
      exclusions: [],
    });
    return buildPresetPayload(next);
  };

  return [
    // {
    //   id: "monthly-today",
    //   label: `Every month on the ${ordinal(dayOfMonth)}`,
    //   apply: monthlyTodayApply,
    //   payload: materialize(monthlyTodayApply),
    // },
    // {
    //   id: "monthly-1st",
    //   label: "Every month on the 1st",
    //   apply: monthly1stApply,
    //   payload: materialize(monthly1stApply),
    // },
    // {
    //   id: "monthly-15th",
    //   label: "Every month on the 15th",
    //   apply: monthly15thApply,
    //   payload: materialize(monthly15thApply),
    // },
    {
      id: "weekly-today",
      label: `Every week on ${weekdayName}`,
      apply: weeklyTodayApply,
      payload: materialize(weeklyTodayApply),
    },
    {
      id: "weekly-on",
      label: "Weekly on",
    },
    // {
    //   id: "daily",
    //   label: "Every day",
    //   apply: dailyApply,
    //   payload: materialize(dailyApply),
    // },
    {
      id: "weekdays",
      label: "Weekdays (Mon–Fri)",
      apply: weekdaysApply,
      payload: materialize(weekdaysApply),
    },

    // {
    //   id: "first-friday",
    //   label: "First Friday of every month",
    //   apply: firstFridayApply,
    //   payload: materialize(firstFridayApply),
    // },
    {
      id: "first-monday",
      label: "First Monday of every month",
      apply: firstMondayApply,
      payload: materialize(firstMondayApply),
    },
    {
      id: "weekly-monday",
      parentId: "weekly-on",
      label: "Monday",
      apply: weeklyMondayApply,
      payload: materialize(weeklyMondayApply),
    },
    {
      id: "weekly-friday",
      parentId: "weekly-on",
      label: "Friday",
      apply: weeklyFridayApply,
      payload: materialize(weeklyFridayApply),
    },
    {
      id: "weekly-saturday",
      parentId: "weekly-on",
      label: "Saturday",
      apply: weeklySaturdayApply,
      payload: materialize(weeklySaturdayApply),
    },
    // {
    //   id: "last-friday",
    //   label: "Last Friday of every month",
    //   apply: lastFridayApply,
    //   payload: materialize(lastFridayApply),
    // },
    // {
    //   id: "last-monday",
    //   label: "Last Monday of every month",
    //   apply: lastMondayApply,
    //   payload: materialize(lastMondayApply),
    // },
    {
      id: "custom",
      label: "Custom...",
    },
  ];
}
